import React, { useEffect, useMemo, useState } from 'react';
import { Source, Layer, useMap } from 'react-map-gl';
import { flightLayerStyle, shadowLayerStyle, tailLayerStyle, hiddenTailLayerStyle } from './styles';
import { useAddMapEventFunction } from 'src/@realtime/hooks';
import { loadMapSprites } from 'src/@realtime/utils';

import { PulsingSelectionLayer } from '../flightSelectionLayer/pulsingSelectionLayer';
import { AltitudeSource, RealtimeLayerIds } from 'src/@realtime/constants';
import { useFlightDataContext } from 'src/@realtime/contexts';
import { MapActionType, useMapContext } from 'src/@realtime/contexts/map';
import { useBuildFlightData, useMatchFlightPosition } from './hooks';
import { useConfigSelectors } from 'src/app/reducers';
import { SelectedFlightTag } from './selectedFlightTag';
import { createClickFunction, createHoverFunction } from './actions';
import { ToolTipSelectionLayer } from '../flightSelectionLayer';

export const FlightLayer = (): JSX.Element => {
  const { current: map } = useMap();
  const configSelectors = useConfigSelectors();
  const selectedTrackTheme = configSelectors.getTheme('operations');
  const {
    state: { selectedTracks },
    dispatch: flightDispatch,
  } = useFlightDataContext();
  const {
    state: { isSpritesLoaded },
    dispatch: mapDispatch,
  } = useMapContext();
  const { flightData, tailData } = useBuildFlightData();
  const findPoint = useMatchFlightPosition();

  const [hoverTooltipData, setHoverTooltipData] = useState<{
    coordinates: GeoJSON.Position;
    altitude?: number;
    altitudeSource: AltitudeSource;
    trackId: string;
    speed?: number;
  } | null>(null);

  const clickFunction = useMemo(() => createClickFunction(flightDispatch), [flightDispatch]);
  const hoverFunction = useMemo(() => createHoverFunction(setHoverTooltipData, findPoint), [
    flightDispatch,
    findPoint,
  ]);
  useAddMapEventFunction(clickFunction);
  useAddMapEventFunction(hoverFunction);

  // Collect the selected flights info required for Selections
  const selectedFeatures = useMemo(
    () =>
      flightData.features.filter(feature => selectedTracks.includes(feature.properties?.trackId)),
    [flightData, selectedTracks]
  );

  loadMapSprites(selectedTrackTheme, () => {
    mapDispatch({
      type: MapActionType.SET_IS_SPRITES_LOADED,
      payload: true,
    });
  });

  useEffect(() => {
    if (!map) {
      return;
    }

    map.getCanvas().style.cursor = hoverTooltipData ? 'pointer' : 'default';

    return () => {
      map.getCanvas().style.cursor = 'default'; // Reset cursor on cleanup
    };
  }, [map, hoverTooltipData]); // Dependencies

  return (
    <>
      {isSpritesLoaded && (
        <>
          <Source id={RealtimeLayerIds.tailLayer} type="geojson" data={tailData}>
            <Layer {...tailLayerStyle} />
            <Layer {...hiddenTailLayerStyle} />
          </Source>
          <Source id={RealtimeLayerIds.flightLayer} type="geojson" data={flightData}>
            <Layer {...shadowLayerStyle} />
            <Layer {...flightLayerStyle} />
          </Source>
        </>
      )}
      {hoverTooltipData && <ToolTipSelectionLayer {...hoverTooltipData} />}
      {selectedTracks.length > 0 && (
        <>
          <PulsingSelectionLayer
            selectedTracks={selectedTracks}
            features={{
              type: 'FeatureCollection',
              features: selectedFeatures, // To know the position of the AC the user selected we pass the selectedFeatures containing coordinates.
            }}
          />
          <SelectedFlightTag trackIds={selectedTracks} />
        </>
      )}
    </>
  );
};
